import PageBackTitle from "../../Component/PageBackTitle";
import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import Select from "react-select";
import { HiOutlinePlus } from "react-icons/hi";
import { IoSearchOutline } from "react-icons/io5";
import { TbRefresh } from "react-icons/tb";
import Pagination from "../../Component/Pagination";
import { hrRecordsApi } from "../../Services/hrRecordsApi";
import {
  FITNESS_STATUS_OPTIONS,
  buildEmployeeSelectOptions,
  buildSimpleOptions,
  formatDateOnlyIST,
  formatDateTimeIST,
  getDuePillStyle,
  getFitnessPillStyle,
} from "../hrRecordsHelpers";
import "../hrRecords.css";
import "../../tables/User.css";

const MEDICAL_STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "expiring_soon", label: "Expiring Soon" },
  { value: "expired", label: "Expired" },
];

const SELECT_MENU_STYLES = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

function MedicalReportsListPage({ isSidebarExpanded }) {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [medicalTypes, setMedicalTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalItems, setTotalItems] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [selectedMedicalType, setSelectedMedicalType] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedFitnessStatus, setSelectedFitnessStatus] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);

  const fetchLookups = useCallback(async () => {
    try {
      const [employeeResponse, typeResponse] = await Promise.all([
        hrRecordsApi.fetchEmployeesDropdown(),
        hrRecordsApi.getMedicalTypes(),
      ]);
      setEmployees(buildEmployeeSelectOptions(employeeResponse.data || []));
      setMedicalTypes(buildSimpleOptions(typeResponse || [], "type_name", "type_name"));
    } catch (error) {
      console.error("Failed to load medical report filters:", error);
    }
  }, []);

  const fetchReports = useCallback(async (
    currentPage = page,
    currentItemsPerPage = itemsPerPage,
    filters = {},
  ) => {
    const {
      employee = selectedEmployee,
      fitnessStatus = selectedFitnessStatus,
      medicalType = selectedMedicalType,
      reportStatus = selectedStatus,
      search = searchInput,
    } = filters;

    try {
      setLoading(true);
      const response = await hrRecordsApi.listMedicalReports({
        page: currentPage,
        per_page: currentItemsPerPage,
        search: search || undefined,
        medical_type: medicalType?.value || undefined,
        status: reportStatus?.value || undefined,
        fitness_status: fitnessStatus?.value || undefined,
        actual_man_power_id: employee?.value || undefined,
      });
      setReports(response.items || []);
      setTotalItems(response.total_items || 0);
    } catch (error) {
      console.error("Failed to load medical reports:", error);
      showSnackbar("Failed to load medical reports", "error");
    } finally {
      setLoading(false);
    }
  }, [
    itemsPerPage,
    page,
    searchInput,
    selectedEmployee,
    selectedFitnessStatus,
    selectedMedicalType,
    selectedStatus,
    showSnackbar,
  ]);

  useEffect(() => {
    fetchLookups();
  }, [fetchLookups]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      fetchReports(page, itemsPerPage);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [fetchReports, itemsPerPage, page]);

  const formatCreatedAtIST = (value) =>
    value
      ? `${formatDateTimeIST(value)} IST`
      : "NA";
  const selectMenuPortalTarget =
    typeof document !== "undefined" ? document.body : null;

  return (
    <>
      <div
        className={`data-list-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-list-content">
          <div className="data-list-header">
            <div className="data-list-header-content hr-medical-reports-header-content">
              <PageBackTitle>Medical Reports</PageBackTitle>
              <div className="data-list-heading-content-right-side hr-records-toolbar hr-medical-reports-toolbar">
                <div className="dropdown-container hr-records-medical-list-filter">
                  <Select
                    options={medicalTypes}
                    value={selectedMedicalType}
                    onChange={(value) => {
                      setSelectedMedicalType(value);
                      setPage(1);
                    }}
                    placeholder="Medical Type"
                    isSearchable
                    isClearable
                    menuPortalTarget={selectMenuPortalTarget}
                    menuPosition="fixed"
                    styles={SELECT_MENU_STYLES}
                  />
                </div>
                <div className="dropdown-container hr-records-medical-list-filter">
                  <Select
                    options={MEDICAL_STATUS_OPTIONS}
                    value={selectedStatus}
                    onChange={(value) => {
                      setSelectedStatus(value);
                      setPage(1);
                    }}
                    placeholder="Status"
                    isClearable
                    menuPortalTarget={selectMenuPortalTarget}
                    menuPosition="fixed"
                    styles={SELECT_MENU_STYLES}
                  />
                </div>
                <div className="dropdown-container hr-records-medical-list-filter">
                  <Select
                    options={FITNESS_STATUS_OPTIONS}
                    value={selectedFitnessStatus}
                    onChange={(value) => {
                      setSelectedFitnessStatus(value);
                      setPage(1);
                    }}
                    placeholder="Fitness Status"
                    isClearable
                    menuPortalTarget={selectMenuPortalTarget}
                    menuPosition="fixed"
                    styles={SELECT_MENU_STYLES}
                  />
                </div>
                <div className="dropdown-container hr-records-medical-list-filter">
                  <Select
                    options={employees}
                    value={selectedEmployee}
                    onChange={(value) => {
                      setSelectedEmployee(value);
                      setPage(1);
                    }}
                    placeholder="Employee"
                    isSearchable
                    isClearable
                    menuPortalTarget={selectMenuPortalTarget}
                    menuPosition="fixed"
                    styles={SELECT_MENU_STYLES}
                  />
                </div>
                <div className="search-bar-container hr-medical-reports-search">
                  <input
                    className="search-bar"
                    placeholder="Search medical reports..."
                    value={searchInput}
                    onChange={(event) => {
                      setSearchInput(event.target.value);
                      setPage(1);
                    }}
                  />
                  <IoSearchOutline className="search-bar-icon" />
                </div>
                <Tooltip title="New Medical Report" arrow>
                  <button
                    className="new-button"
                    onClick={() => navigate("/add-hr-medical-report")}
                  >
                    <HiOutlinePlus className="new-plus-icon" /> New
                  </button>
                </Tooltip>
                <Tooltip title="Refresh" arrow>
                  <button
                    className="refresh-button"
                    onClick={() => {
                      setSearchInput("");
                      setSelectedMedicalType(null);
                      setSelectedStatus(null);
                      setSelectedFitnessStatus(null);
                      setSelectedEmployee(null);
                      setPage(1);
                      fetchReports(1, itemsPerPage, {
                        employee: null,
                        fitnessStatus: null,
                        medicalType: null,
                        reportStatus: null,
                        search: "",
                      });
                    }}
                  >
                    <TbRefresh className="refresh-icon" />
                  </button>
                </Tooltip>
              </div>
            </div>
          </div>
          <div className="header-separator"></div>

          <div className="data-list-body">
            <div className="data-list-scroll-body">
              <div className="data-list-table">
                {loading ? (
                  <div className="loader-container">
                    <div className="loader"></div>
                    <div>Loading...</div>
                  </div>
                ) : reports.length > 0 ? (
                  <table className="table multilevel-data">
                    <thead>
                      <tr>
                        <th>SR. NO.</th>
                        <th>EMPLOYEE</th>
                        <th>MEDICAL TYPE</th>
                        <th>EXAM DATE</th>
                        <th>REPORT NO.</th>
                        <th>FITNESS</th>
                        <th>STATUS</th>
                        <th>VALID TO</th>
                        <th>NEXT DUE</th>
                        <th>CREATED AT</th>
                        <th>ACTION</th>
                      </tr>
                    </thead>
                    <tbody className="data-list-scroll-bodys">
                      {reports.map((report, index) => (
                        <tr key={report.report_id || report.id} className="hoverable-row">
                          <td>{(page - 1) * itemsPerPage + index + 1}</td>
                          <td>
                            {report.person_name || "NA"}
                            <br />
                            <span style={{ color: "#68768a", fontSize: "12px" }}>
                              {report.ec_no || "NA"}
                            </span>
                          </td>
                          <td>{report.medical_type || "NA"}</td>
                          <td>{formatDateOnlyIST(report.examination_date)}</td>
                          <td>{report.report_number || "NA"}</td>
                          <td className="center-alignment">
                            <span
                              className="hr-records-pill"
                              style={getFitnessPillStyle(report.fitness_status)}
                            >
                              {report.fitness_status || "NA"}
                            </span>
                          </td>
                          <td className="center-alignment">
                            <span
                              className="hr-records-pill"
                              style={getDuePillStyle(report.status)}
                            >
                              {(report.status || "NA").replace(/_/g, " ")}
                            </span>
                          </td>
                          <td>{formatDateOnlyIST(report.valid_to)}</td>
                          <td>{formatDateOnlyIST(report.next_due_date)}</td>
                          <td>{formatCreatedAtIST(report.created_at)}</td>
                          <td className="center-alignment">
                            <div className="hr-records-inline-actions">
                              <button
                                className="button-bg"
                                onClick={() =>
                                  navigate(
                                    `/hr-medical-report-view/${report.report_id || report.id}`,
                                  )
                                }
                              >
                                View
                              </button>
                              <Link
                                to={`/edit-hr-medical-report/${report.report_id || report.id}`}
                              >
                                <button className="button-bg">Edit</button>
                              </Link>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="no-users-container">
                    <div className="no-users-message">No medical reports found</div>
                  </div>
                )}
              </div>

              <Pagination
                key={`${selectedMedicalType?.value || "all"}-${
                  selectedStatus?.value || "all"
                }-${selectedFitnessStatus?.value || "all"}-${
                  selectedEmployee?.value || "all"
                }-${searchInput}`}
                showPerPage={itemsPerPage}
                total={totalItems}
                onPaginationChange={(currentPage, perPage) => {
                  setPage(currentPage);
                  setItemsPerPage(perPage);
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert severity={snackbarSeverity}>{snackbarMessage}</MuiAlert>
      </Snackbar>
    </>
  );
}

export default MedicalReportsListPage;
